
import React, { useState, useEffect } from 'react';
import { PLANS, STEPS, MOCK_CLIENTS } from '../constants';
import { CheckCircle2, Star, Zap, ShoppingBag, ShieldCheck, HeartHandshake, Eye, EyeOff, Lock, Unlock, Loader2, Save, Settings, ChevronRight, MoreVertical, Search, Plus, Sliders, Briefcase, ChevronDown, Activity, Globe, MessageSquare, Target, Layout, Shield, Building2
} from 'lucide-react';
import { Responsibility, StepGovernance, UserRole, Client, User } from '../types';
import { supabase_get_client_branding, supabase_update_client_branding, supabase, supabase_get_clients } from '../services/supabaseService';

const SubscriptionManager: React.FC<{user: User | null}> = ({ user }) => {
  const [governance, setGovernance] = useState<Record<string, StepGovernance>>({});
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data: profile } = await supabase.from('profiles').select('client_id, role').eq('id', session.user.id).single();
        
        const isStaff = profile?.role === UserRole.ADMIN || profile?.role === UserRole.RECRUITER;
        setIsAdmin(isStaff);

        if (isStaff) {
          const clientList = await supabase_get_clients();
          setClients(clientList || []);
          const initialCid = profile?.client_id || clientList?.[0]?.id;
          setSelectedClientId(initialCid);
        } else if (profile?.client_id) {
          setSelectedClientId(profile.client_id);
        }
      }
    };
    init();
  }, [user]);

  useEffect(() => {
    const loadConfig = async () => {
        if (!selectedClientId) return;
        setLoading(true);
        
        const branding = await supabase_get_client_branding(selectedClientId);
        if (branding?.stepGovernance) {
            setGovernance(branding.stepGovernance);
        } else {
            const defaults: Record<string, StepGovernance> = {};
            STEPS.forEach(step => {
                defaults[step.id] = { visibility: 'everyone', mode: 'full', responsibility: 'Klant' };
            });
            setGovernance(defaults);
        }
        setLoading(false);
    };
    loadConfig();
  }, [selectedClientId, user]);

  const updateGovernance = (stepId: string, field: keyof StepGovernance, value: any) => {
    setGovernance(prev => ({
      ...prev,
      [stepId]: {
        ...(prev[stepId] || { visibility: 'everyone', mode: 'full', responsibility: 'Klant' }),
        [field]: value
      }
    }));
  };

  const handleSaveGovernance = async () => {
    if (!selectedClientId) return;
    setIsSaving(true);
    try {
      const branding = await supabase_get_client_branding(selectedClientId) || {};
      await supabase_update_client_branding(selectedClientId, { ...branding, stepGovernance: governance });
      alert("Workflow Governance opgeslagen!");
    } catch (e) {
      console.error(e);
      alert("Fout bij opslaan.");
    } finally {
      setIsSaving(false);
    }
  };

  if (loading && !selectedClientId) return <div className="p-20 flex justify-center"><Loader2 className="animate-spin text-red-600" /></div>;

  return (
    <div className="p-4 lg:p-6 space-y-4 animate-fadeIn max-w-[1600px] mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
        <div>
            <h1 className="text-xl font-black text-slate-900 tracking-tight leading-tight flex items-center gap-2">
                <Shield className="text-[#e51b23] shrink-0" size={24} />
                Governance
            </h1>
            <p className="text-xs text-slate-500 font-medium mt-1">Configure workflow permissions and responsibilities.</p>
        </div>
        {isAdmin && (
            <div className="relative w-full md:w-64">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <select 
                    value={selectedClientId || ''} 
                    onChange={(e) => setSelectedClientId(e.target.value)}
                    className="w-full pl-10 pr-8 py-3 bg-white border border-slate-200 rounded-xl font-bold text-xs text-slate-700 outline-none focus:ring-2 focus:ring-[#e51b23] appearance-none cursor-pointer shadow-sm"
                >
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
            </div>
        )}
      </div>

      <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-900 text-white rounded-xl flex items-center justify-center shadow-sm">
              <ShieldCheck size={20} />
            </div>
            <div>
              <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest">Workflow Matrix</h2>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Config for: <span className="text-[#e51b23]">{clients.find(c => c.id === selectedClientId)?.name || "Selected Client"}</span>
              </p>
            </div>
          </div>
          <button 
            onClick={handleSaveGovernance}
            disabled={isSaving || !selectedClientId}
            className="flex items-center gap-2 bg-[#e51b23] text-white px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-black transition-all shadow-lg active:scale-95 disabled:opacity-50"
          >
            {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} 
            Save Config
          </button>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
              <div className="p-20 flex flex-col items-center justify-center">
                  <Loader2 className="animate-spin text-red-600 mb-2" size={32} />
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Loading Settings...</p>
              </div>
          ) : (
            <table className="w-full text-left border-collapse">
                <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-400 border-b border-gray-100">
                <tr>
                    <th className="px-6 py-4 pl-8">Step</th>
                    <th className="px-6 py-4">Responsibility</th>
                    <th className="px-6 py-4">Visibility</th>
                    <th className="px-6 py-4 pr-8">Access Mode</th>
                </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                {STEPS.map(step => {
                    const config = governance[step.id] || { visibility: 'everyone', mode: 'full', responsibility: 'Klant' };
                    return (
                    <tr key={step.id} className="hover:bg-slate-50/50 transition-colors group">
                        <td className="px-6 py-4 pl-8">
                        <div className="flex items-center gap-4">
                            <div className="p-2 bg-slate-50 rounded-lg text-slate-400 group-hover:bg-white group-hover:text-slate-600 border border-transparent group-hover:border-slate-100 transition-all">
                                {React.cloneElement(step.icon as React.ReactElement<any>, { size: 16 })}
                            </div>
                            <span className="text-xs font-bold text-slate-800 uppercase tracking-tight">{step.label}</span>
                        </div>
                        </td>
                        <td className="px-6 py-4">
                        <div className="relative w-40">
                            <select 
                                value={config.responsibility}
                                onChange={(e) => updateGovernance(step.id, 'responsibility', e.target.value)}
                                className={`w-full pl-3 pr-8 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest outline-none border cursor-pointer appearance-none ${
                                    config.responsibility === 'Werkplaats' ? 'bg-indigo-50 text-indigo-700 border-indigo-100' : 
                                    config.responsibility === 'Gedeeld' ? 'bg-purple-50 text-purple-700 border-purple-100' :
                                    'bg-white text-slate-600 border-slate-200'
                                }`}
                            >
                                <option value="Klant">Client</option>
                                <option value="Werkplaats">Werfplaats</option>
                                <option value="Gedeeld">Shared</option>
                            </select>
                            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 opacity-50 pointer-events-none" size={12} />
                        </div>
                        </td>
                        <td className="px-6 py-4">
                        <div className="relative w-40">
                            <select 
                                value={config.visibility}
                                onChange={(e) => updateGovernance(step.id, 'visibility', e.target.value)}
                                className={`w-full pl-3 pr-8 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest outline-none border cursor-pointer appearance-none ${
                                    config.visibility === 'werfplaats' ? 'bg-red-50 text-red-700 border-red-100' : 
                                    'bg-white text-slate-600 border-slate-200'
                                }`}
                            >
                                <option value="everyone">Everyone</option>
                                <option value="recruiters">Recruiters Only</option>
                                <option value="werfplaats">Werfplaats Only</option>
                            </select>
                            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 opacity-50 pointer-events-none" size={12} />
                        </div>
                        </td>
                        <td className="px-6 py-4 pr-8">
                        <div className="relative w-40">
                            <select 
                                value={config.mode}
                                onChange={(e) => updateGovernance(step.id, 'mode', e.target.value)}
                                className={`w-full pl-3 pr-8 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest outline-none border cursor-pointer appearance-none ${
                                    config.mode === 'results' ? 'bg-amber-50 text-amber-700 border-amber-100' : 
                                    'bg-white text-slate-600 border-slate-200'
                                }`}
                            >
                                <option value="full">Full Control</option>
                                <option value="results">Read Only</option>
                            </select>
                            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 opacity-50 pointer-events-none" size={12} />
                        </div>
                        </td>
                    </tr>
                    );
                })}
                </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManager;
